/**
 * Created by jonas on 2021-02-09.
 *
 * Super basic Symbol implementation that extends PIXI.Sprite
 *
 */

import {KitchenDramaSushiMania} from "../KitchenDramaSushiMania";
import {KitchenDramaSushiManiaStatics} from "../utils/KitchenDramaSushiManiaStatics";
import {gsap} from "gsap";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {Positionable, SimpleSymbolState} from "../../core/reel/simplereel/SimpleReelModel";
import {KitchenDramaSliceAnimation} from "./symbolparts/KitchenDramaSliceAnimation";
import {SliceData, SlicePairs} from "./symbolparts/SlicePairs";
import {MathUtils} from "../utils/MathUtils";
import {KitchenDramaSushiManiaSound} from "../sound/KitchenDramaSushiManiaSound";
import {KitchenDramaSushiManiaGameAssets} from "../KitchenDramaSushiManiaGameAssets";

export enum KitchenDramaSushiManiaSymbolStates {
    NORMAL,
    SPIN,
    WIN,
    NO_WIN

}

export class KitchenDramaSushiManiaSymbol extends PIXI.Container implements Positionable {
    private _symbolName: string;
    private _overlay: PIXI.Sprite;
    private _jumpAnimation: TimelineSprite;
    private _noWinSprite: PIXI.Sprite;
    private _normalScale: PIXI.Point;
    private _sliceContainer: PIXI.Container;
    private _flipFlop: boolean;
    private _sliceAnimations: KitchenDramaSliceAnimation[] = [];
    private _sliceAbove: PIXI.Sprite | TimelineSprite;
    private _sliceBehind: PIXI.Sprite;
    private _lastSliceAnimation: KitchenDramaSliceAnimation;
    private _view: PIXI.Container;
    private _ninjaContainer: PIXI.Container;
    private _kamisabiContainer: PIXI.Container;

    constructor(symbolName: string, initState: KitchenDramaSushiManiaSymbolStates, reelId: number) {
        super();

        this._flipFlop = (reelId % 2) == 0;

        this._symbolName = symbolName;

        this._normalScale = new PIXI.Point(0.9, 0.9);

        this._overlay = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._overlay.tint = 0xff0000;
        this._overlay.alpha = 0.3;
        this._overlay.width = KitchenDramaSushiManiaStatics.SYMBOL_WIDTH;
        this._overlay.height = KitchenDramaSushiManiaStatics.SYMBOL_HEIGHT;

        this._jumpAnimation = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures(this._symbolName + "_jump"), 30);
        this._jumpAnimation.anchor.set(0.5, 1);
        this._jumpAnimation.position.set(KitchenDramaSushiManiaStatics.SYMBOL_WIDTH * 0.5, KitchenDramaSushiManiaStatics.SYMBOL_HEIGHT);
        this._jumpAnimation.scale.set(this._normalScale.x, this._normalScale.y);

        this._noWinSprite = new PIXI.Sprite(KitchenDramaSushiMania.assetLoader.getTextures("nowin/" + this._symbolName + "_nowin")[0]);
        this._noWinSprite.anchor.set(0.5, 1);
        this._noWinSprite.position.set(KitchenDramaSushiManiaStatics.SYMBOL_WIDTH * 0.5, KitchenDramaSushiManiaStatics.SYMBOL_HEIGHT);
        this._noWinSprite.scale.set(this._normalScale.x, this._normalScale.y);
        this._noWinSprite.visible = false;


        this._ninjaContainer = new PIXI.Container();
        this._ninjaContainer.name = "_ninjaContainer";
        this._ninjaContainer.position.set(KitchenDramaSushiManiaStatics.SYMBOL_WIDTH * 0.5, KitchenDramaSushiManiaStatics.SYMBOL_HEIGHT - 200);
        this._ninjaContainer.scale.set(this._normalScale.x, this._normalScale.y);

        this._sliceContainer = new PIXI.Container();
        this._sliceContainer.name = "_sliceContainer";
        this._sliceContainer.position.set(KitchenDramaSushiManiaStatics.SYMBOL_WIDTH * 0.5, KitchenDramaSushiManiaStatics.SYMBOL_HEIGHT - 200);
        this._sliceContainer.scale.set(this._normalScale.x, this._normalScale.y);

        this._kamisabiContainer = new PIXI.Container();
        this._kamisabiContainer.name = "_kamisabiContainer";
        this._kamisabiContainer.position.set(KitchenDramaSushiManiaStatics.SYMBOL_WIDTH * 0.5, KitchenDramaSushiManiaStatics.SYMBOL_HEIGHT - 16);
        this._kamisabiContainer.scale.set(this._normalScale.x, this._normalScale.y);

        this.setOffset();

        this._view = new PIXI.Container();

        this._view.addChild(this._jumpAnimation);
        this._view.addChild(this._noWinSprite);
        this._view.addChild(this._ninjaContainer);
        this._view.addChild(this._sliceContainer);
        this._view.addChild(this._kamisabiContainer);

        this.addChild(this._view);

        //this.setState(initState);
    }


    destroy(options?: { children?: boolean; texture?: boolean; baseTexture?: boolean }) {
        this._sliceAnimations = [];
        super.destroy(options);
    }

    setSymbolState(state: SimpleSymbolState): void {
        switch (state) {
            case SimpleSymbolState.NORMAL:
                this.normal();
                break;
            case SimpleSymbolState.DROP_IN:
                this.dropIn();
                break;
            case SimpleSymbolState.DROP_OUT:
                this.dropOut();
                break;
            case SimpleSymbolState.COLLAPSE:
                this.dropDown();
                break;

        }
    }

    public noWin(): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            this._noWinSprite.visible = true;
            this._noWinSprite.alpha = 0;
        });
        tl.add(gsap.to(this._noWinSprite, {duration: 0.6, alpha: 0.6}));
        tl.add(gsap.to(this._jumpAnimation, {duration: 0.6, alpha: 1}));
        return tl;
    }

    public backToNormal(): void {
        const tl = gsap.timeline();
        tl.add(() => {
            this._noWinSprite.visible = false;
            this._noWinSprite.alpha = 0;
        });
    }

    public normal(): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            this._jumpAnimation.setFrame(0);
        });
        return tl;
    }

    public dropOut(): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.timeScale(KitchenDramaSushiManiaStatics.TIME_SCALE);
        let jump = this._jumpAnimation.getAnimation([0, 26]);
        let jumpRev = this._jumpAnimation.getAnimation([26, 17]);

        tl.add(jump);
        tl.add(jumpRev);
        tl.add(gsap.to(this._jumpAnimation.scale, {
            duration: 0.15,
            x: 0.85,
            y: 1.65,
            ease: "power1.in"
        }), 1);

        tl.add(gsap.to(this._view.position, {
            duration: 1,
            y: this._view.position.y + 120,
            ease: "none"
        }), 0.433);


        return tl;
    }

    public dropIn(): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.timeScale(KitchenDramaSushiManiaStatics.TIME_SCALE);

        let jump = this._jumpAnimation.getAnimation([21, 0]);

        tl.add(jump);


        return tl;
    }

    public dropDown(): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.timeScale(KitchenDramaSushiManiaStatics.TIME_SCALE);
        let jump = this._jumpAnimation.getAnimation([0, 19]);
        let jumpRev = this._jumpAnimation.getAnimation([19, 0]);

        tl.add(jump);
        tl.add(jumpRev);

        return tl;
    }

    public playWin(): gsap.core.Timeline {
        const tl = gsap.timeline();
        //tl.timeScale(KitchenDramaSushiManiaStatics.TIME_SCALE);
        let jump = this._jumpAnimation.getAnimation([0, 19]);
        tl.add(jump);

        tl.add(() => this.moveToTop());
        tl.add(gsap.to(this._view.position, {
            duration: 1,
            y: this._view.position.y + 75,
            ease: "power1.out"
        }), 0.433);
        return tl;
    }

    public playSliceAnimation(wasSkipped: Function): gsap.core.Timeline {
        const symName = this._symbolName;
        const isLowWin = KitchenDramaSushiManiaStatics.isLowSymbol(this._symbolName);
        let sliceData: SliceData;
        if (isLowWin) {
            let index = this._flipFlop ? 0 : 1;
            this._flipFlop = !this._flipFlop;
            sliceData = SlicePairs.SLICE_PAIRS[symName][index]; //0 = Left, 1 = right
        } else {
            sliceData = SlicePairs.getRandomSliceForSym(symName);
        }

        let sliceAnimation: KitchenDramaSliceAnimation = new KitchenDramaSliceAnimation(sliceData, isLowWin);
        let tl: gsap.core.Timeline = gsap.timeline();

        if (symName == "M3") {
            sliceAnimation.position.set(0, -40);
        }

        tl.add(() => {
            this._sliceContainer.addChild(sliceAnimation);
            this._sliceContainer.visible = true;
            this._jumpAnimation.hide();
        });

        if (this._sliceAnimations.length >= 1) {
            let temp = this._sliceAnimations[this._sliceAnimations.length - 1];
            if (isLowWin) {
                tl.add(() => {
                    sliceAnimation.sliceBehind.rotation = temp.sliceBehind.rotation
                });
            }

            tl.add(() => {
                temp.hide();
                this._sliceContainer.removeChild(temp);
            });
        }

        tl.add(() => sliceAnimation.show());

        let klinkStart: number = tl.totalDuration();
        tl.add(() => this.playSliceSound(wasSkipped()));
        tl.add(sliceAnimation.swoosh.getAnimation());
        tl.add(() => sliceAnimation.swoosh.hide());

        if (isLowWin) {
            klinkStart += 0.1;
            let klinkAnim: gsap.core.Timeline = gsap.timeline();
            klinkAnim.add(() => sliceAnimation.klink.show());
            klinkAnim.add(sliceAnimation.klink.getAnimation());
            klinkAnim.add(() => sliceAnimation.klink.hide());
            tl.add(klinkAnim, klinkStart);

            let targetRot = sliceData.turn == "left" ? -0.45 : 0.45;
            let targetSlowRot = sliceData.turn == "left" ? -0.55 : 0.55;

            tl.add(gsap.to(sliceAnimation.sliceBehind, {
                duration: 0.01,
                rotation: targetRot,
                ease: "none"
            }), klinkStart);
            tl.add(gsap.to(sliceAnimation.sliceBehind, {
                duration: 1.0,
                rotation: targetSlowRot,
                ease: "none"
            }), klinkStart + 0.01);

        }


        this._sliceAnimations.push(sliceAnimation);
        this._sliceAbove = sliceAnimation.sliceAbove;
        this._sliceBehind = sliceAnimation.sliceBehind;
        this._lastSliceAnimation = sliceAnimation;

        return tl;
    }

    public playFallDown(): gsap.core.Timeline {
        //Always after slice.
        let slideOffset = 10;
        let midWinRotation = MathUtils.randomNumberFromInterval(2, 4);
        let fallOffset = MathUtils.randomIntFromInterval(150, 200);

        const isLowWin = KitchenDramaSushiManiaStatics.isLowSymbol(this._symbolName);

        let tl = gsap.timeline();
        if (!isLowWin) {
            let slideLeft: PIXI.Sprite;
            let slideRight: PIXI.Sprite;

            if (this._lastSliceAnimation.data.switchSliceAssets) {
                slideLeft = this._sliceAbove;
                slideRight = this._sliceBehind;
            } else {
                slideLeft = this._sliceBehind;
                slideRight = this._sliceAbove;
            }

            tl.add([
                gsap.to(slideLeft, {duration: 0.8, x: slideLeft.x - slideOffset, ease: "power1.out"}),
                gsap.to(slideRight, {duration: 0.8, x: slideRight.x + slideOffset, ease: "power1.out"}),
            ]);

            tl.add([
                gsap.to(slideLeft, {duration: 0.5, y: 2000, ease: "power1.in"}),
                gsap.to(slideLeft, {duration: 0.5, x: slideLeft.x - fallOffset, ease: "power1.in"}),
                gsap.to(slideLeft, {duration: 0.5, rotation: -midWinRotation, ease: "power1.in"}),
                gsap.to(slideRight, {duration: 0.5, y: 2000, ease: "power1.in"}),
                gsap.to(slideRight, {duration: 0.5, x: slideRight.x + fallOffset, ease: "power1.in"}),
                gsap.to(slideRight, {duration: 0.5, rotation: midWinRotation, ease: "power1.in"}),
            ]);
        } else {
            let rotation = Math.PI * 6;
            let firstRot = 0.65;

            if (this._lastSliceAnimation.data.turn == "left") {
                rotation = -Math.PI * 6;
                firstRot = -0.65;
                fallOffset = fallOffset / 2;
            }

            tl.add([
                gsap.to(this._sliceBehind, {duration: 0.5, rotation: firstRot, ease: "power1.in"})
            ]);
            tl.add([
                gsap.to(this._sliceBehind, {duration: 0.8, y: 2000, ease: "power1.in"}),
                gsap.to(this._sliceBehind, {duration: 0.8, x: this._sliceBehind.x + fallOffset, ease: "none"}),
                gsap.to(this._sliceBehind, {duration: 0.8, rotation: rotation, ease: "none"})
            ]);
        }

        return tl;
    }

    public playNinjaInit(): gsap.core.Timeline {
        const tl = gsap.timeline();
        const ninjaJump = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures("ninjaWildJump"));
        ninjaJump.anchor.set(0.5, 1);
        ninjaJump.position.set(-8, KitchenDramaSushiManiaStatics.SYMBOL_HEIGHT + 20);

        tl.add(() => {
            this._ninjaContainer.addChild(ninjaJump);
            this._jumpAnimation.hide();
        });
        tl.add(ninjaJump.getAnimation());
        return tl;
    }

    public transformToNinjaWild(): gsap.core.Timeline {
        const tl = gsap.timeline();
        const ninjaSplit = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures("ninjaWildSplit"));
        ninjaSplit.anchor.set(0.5, 1);
        ninjaSplit.position.set(-8, KitchenDramaSushiManiaStatics.SYMBOL_HEIGHT + 20);

        const ninjaLand = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures("ninjaWildLand"));
        ninjaLand.anchor.set(0.5, 1);
        ninjaLand.position.set(-8, KitchenDramaSushiManiaStatics.SYMBOL_HEIGHT + 20);

        const whitePouff = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures("whitePouff"));
        whitePouff.anchor.set(0.5, 0.5);
        whitePouff.position.set(-20, KitchenDramaSushiManiaStatics.SYMBOL_HEIGHT + -40);

        tl.add(() => {
            this.moveToTop();
            this._ninjaContainer.addChild(ninjaSplit);
            this._jumpAnimation.hide();
        });

        tl.add(ninjaSplit.getAnimation());
        tl.add(() => {
            this._ninjaContainer.addChild(ninjaLand);
            this._ninjaContainer.removeChild(ninjaSplit);
        });
        tl.add(ninjaLand.getAnimation());
        tl.add(() => {
            this._ninjaContainer.removeChild(ninjaLand);
        });

        const whitePouffTl = gsap.timeline();
        whitePouffTl.add(() => {
            this._ninjaContainer.addChild(whitePouff);
        });
        whitePouffTl.add(whitePouff.getAnimation());
        whitePouffTl.add(() => this._jumpAnimation.show(), "<0.1");
        whitePouffTl.add(() => {
            this._ninjaContainer.removeChild(whitePouff);
        });

        tl.add(whitePouffTl, ">-0.15");


        return tl;
    }

    public transformToSushiWild(): gsap.core.Timeline {
        const tl = gsap.timeline();

        const sushiTransform = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures("sushiTransform"));
        sushiTransform.anchor.set(0.5, 1);
        sushiTransform.position.set(KitchenDramaSushiManiaStatics.SYMBOL_WIDTH * 0.5 - 11, KitchenDramaSushiManiaStatics.SYMBOL_HEIGHT + 6.5);
        sushiTransform.scale.set(this._normalScale.x, this._normalScale.y);
        //sushiTransform.position.set(-9, 36);
        sushiTransform.show();

        tl.add(() => {
            this._view.addChild(sushiTransform);
            this._jumpAnimation.hide();
        });

        tl.add(sushiTransform.getAnimation());

        tl.add(() => {
            this._overlay.tint = 0xffffff;
            this._view.removeChild(sushiTransform);
            this._jumpAnimation.show();
        })

        return tl;
    }

    public transformSushiHide(): gsap.core.Timeline {
        let tl: gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this._jumpAnimation, {duration: 0.02, rotation: 0.1, ease: "none"}));
        tl.add(gsap.to(this._jumpAnimation, {duration: 0.02, rotation: -0.1, ease: "none"}));
        tl.add(gsap.to(this._jumpAnimation, {duration: 0.02, rotation: 0.1, ease: "none"}));
        tl.add(gsap.to(this._jumpAnimation, {duration: 0.02, rotation: -0.1, ease: "none"}));
        tl.add(gsap.to(this._jumpAnimation, {duration: 0.02, rotation: 0.0, ease: "none"}));

        tl.add(gsap.to(this._jumpAnimation.scale, {duration: 0.1, x: 0.6, y: 1.2, ease: "none"}));
        tl.add([
            gsap.to(this._jumpAnimation.scale, {duration: 0.05, x: 1.0, y: 0, ease: "none"}),
            gsap.to(this._jumpAnimation, {duration: 0.05, alpha: 0, ease: "none"})
        ]);
        tl.add(() => {
            this._jumpAnimation.alpha = 1;
            this._jumpAnimation.hide();
        })

        return tl;
    }

    public transformHide(duration: number = 0.3): gsap.core.Timeline {
        const tl = gsap.timeline();

        tl.add(() => {
            this._overlay.tint = 0x0000ff;
        })
        tl.add(gsap.to(this._jumpAnimation, {duration: duration, alpha: 0}));
        tl.add(() => {
            this._jumpAnimation.alpha = 1;
            this._jumpAnimation.hide();
        })


        return tl;
    }

    public playKamisabiRemove(duration: number = 0.3): gsap.core.Timeline {

        const shadow = new PIXI.Sprite(KitchenDramaSushiMania.assetLoader.getTextures(KitchenDramaSushiManiaGameAssets.KAMISABI_SHADOW)[0]);
        shadow.visible = false;
        shadow.anchor.set(0.5, 0.5);
        shadow.position.set(0, -40);
        shadow.alpha = 0.0;
        shadow.scale.set(0, 0);


        const fly = new PIXI.Sprite(KitchenDramaSushiMania.assetLoader.getTextures(KitchenDramaSushiManiaGameAssets.KAMISABI_FLY)[0]);
        fly.visible = false;
        fly.anchor.set(0.5, 0.8);
        fly.position.set(0, -1400);


        let melt: TimelineSprite;
        if (KitchenDramaSushiManiaStatics.MID_SYMBOLS.indexOf(this._symbolName) >= 0) {
            melt = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures(KitchenDramaSushiManiaGameAssets.KAMISABI_MID_MELT));
        } else {
            if (this._symbolName == "L1") {
                melt = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures(KitchenDramaSushiManiaGameAssets.KAMISABI_MAYO_MELT));
            } else {
                melt = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures(KitchenDramaSushiManiaGameAssets.KAMISABI_LOW_MELT));
            }
        }
        melt.anchor.set(0.5, 1);
        melt.hide();


        const splat = new TimelineSprite(KitchenDramaSushiMania.assetLoader.getTextures(KitchenDramaSushiManiaGameAssets.KAMISABI_SPLASH));
        splat.anchor.set(0.5, 0.5);
        splat.position.set(-20, -65);
        splat.hide();


        this._kamisabiContainer.addChild(shadow);
        this._kamisabiContainer.addChild(fly);
        this._kamisabiContainer.addChild(splat);
        this._kamisabiContainer.addChild(melt);


        const tl = gsap.timeline();

        tl.add(() => {
            this._overlay.tint = 0x3e522b;
        })

        tl.add(() => {
            shadow.visible = true
        });
        tl.add([
            gsap.to(shadow, 0.15, {alpha: 0.4, ease: "power2.in"}),
            gsap.to(shadow.scale, 0.15, {x: 1.8, y: 1.8, ease: "power2.in"}),
        ]);


        //Remove
        tl.add(() => {
            fly.visible = true
        });
        tl.add([
            gsap.to(shadow, 1.0, {alpha: 1, ease: "power2.in"}),
            gsap.to(shadow.scale, 1.0, {x: 1, y: 1, ease: "power2.in"}),
            gsap.to(fly.position, 0.3, {delay: 0.7, y: 0, ease: "power2.in"})
        ]);
        tl.add(() => {
            shadow.visible = false;
            fly.visible = false;
            this._jumpAnimation.hide();
        })

        tl.add([
            splat.getAnimationAutoShowHide(true, true),
            melt.getAnimationAutoShowHide(true, true)
        ]);


        tl.add(() => {
            this._kamisabiContainer.removeChild(shadow);
            this._kamisabiContainer.removeChild(fly);
            this._kamisabiContainer.removeChild(splat);
            this._kamisabiContainer.removeChild(melt);
        });

        return tl;
    }

    protected setOffset(): void {
        switch (this._symbolName) {
            case "L4":
                break;
            case "L3":
                break;
            case "L2":
                break;
            case "L1":
                break;
            case "M4":
                break;
            case "M3":
                this._noWinSprite.position.y += 15;
                this._jumpAnimation.position.y += 15;
                break;
            case "M2":
                break;
            case "M1":
                this._noWinSprite.position.y += 20;
                this._jumpAnimation.position.y += 20;
                break;
            case "W":
                break;
            case "W2":
                break;
            case "W3":
                this._noWinSprite.position.x += 15;
                this._jumpAnimation.position.x += 15;
                break;
        }
    }

    private moveToTop() {
        if (this.parent) {
            this.parent.addChild(this);
        }
    }

    private playSliceSound(skipped: boolean) {
        if (skipped) {
            return;
        }
        if (KitchenDramaSushiManiaStatics.MID_SYMBOLS.indexOf(this._symbolName) >= 0) {
            KitchenDramaSushiMania.soundPlayer.playSound(KitchenDramaSushiManiaSound.MEDIUM_SLICE_SOUNDS.next());
        } else {
            KitchenDramaSushiMania.soundPlayer.playSound(KitchenDramaSushiManiaSound.LOW_SLICE_SOUNDS.next());
        }
    }

}